// Arbetsmiljöplan (AMP) – mall enligt bifogad BAMS-mall (Dok 12)
// AMP är navet: varje avsnitt är kopplat till ett dokument i Bas-processen.

export type AmpSectionStatus = "not_started" | "in_progress" | "done" | "not_applicable";

export const AMP_STATUS_LABELS: Record<AmpSectionStatus, string> = {
  not_started: "Ej påbörjad",
  in_progress: "Pågår",
  done: "Klar",
  not_applicable: "Ej aktuellt",
};

/** Vilken typ av underlag som kan hämtas in i avsnittet */
export type AmpSourceKind =
  | "project"
  | "organisation"
  | "forhandsanmalan"
  | "sarskilda"
  | "risk"
  | "skr"
  | "workarea"
  | "skyddsanordningar"
  | "ordningsregler"
  | "samordning"
  | "bruksskede"
  | "safety_rules"
  | "meetings"
  | "followup"
  | "work_preparations"
  | "bilagor";

export interface AmpDocRef {
  /** Dokumentnummer i Bas-processen, t.ex. "11" */
  doc: string;
  title: string;
  /** Intern länk i appen (används av hänvisningsraden) */
  link?: "amp" | "risk" | "arbetsomrade" | "utforande" | "samordning" | "bruksskede" | "skr" | "projekt" | "skyddsregler" | "arbetsberedning";
}

export interface AmpContact {
  company?: string;
  orgNumber?: string;
  representative?: string;
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
  period?: string;
}

export interface AmpHotWorkPermit {
  company?: string;
  responsible?: string;
  phone?: string;
  delegated?: string;
  period?: string;
}

export interface AmpSiteOrgRow {
  role: string;
  name: string;
  phone: string;
}

export interface AmpAppendix {
  name: string;
  doc: string;
  included: boolean;
  note?: string;
  /** Etapp och/eller område som bilagan gäller. Tomt = hela projektet. */
  scope?: string;
}

export interface AmpSpecialRisk {
  id: string;
  text: string;
  applicable: boolean | null;
  measures?: string;
  /** Bas-U:s uppföljning under byggskedet */
  executionNote?: string;
  executionDone?: boolean;
}

export interface AmpSignature {
  name: string;
  signatureData?: string;
  date?: string;
  role?: string;
}

/** Generisk fältbeskrivning för ett AMP-avsnitt */
export interface AmpFieldDef {
  key: string;
  label: string;
  type: "text" | "textarea" | "contact" | "hotwork" | "siteOrg" | "riskChecklist" | "appendixList" | "signatureList";
  rows?: number;
  default?: string;
}

export type AmpOwnerRole = "bas_p" | "bas_u" | "shared";

export const AMP_OWNER_LABELS: Record<AmpOwnerRole, string> = {
  bas_p: "Bas-P",
  bas_u: "Bas-U",
  shared: "Bas-P & Bas-U",
};

export interface AmpSectionDef {
  key: string;
  /** Vem som ansvarar för att fylla i avsnittet */
  ownerRole: AmpOwnerRole;
  title: string;
  legalReference: string;
  explanation: string;
  placeholder?: string;
  source?: AmpSourceKind;
  docRefs?: AmpDocRef[];
  fields?: AmpFieldDef[];
}

/** Markörer runt importerat underlag så att egen text aldrig skrivs över. */
export const AMP_IMPORT_START = "<<< Underlag hämtat från Bas-processen";
export const AMP_IMPORT_END = ">>> Slut på hämtat underlag";

export interface AmpSourceMeta {
  doc?: string;
  docTitle?: string;
  count?: number;
  fetchedAt?: string;
  sourceUpdatedAt?: string | null;
}

export const AMP_TEMPLATE: AmpSectionDef[] = [
  {
    key: "bilagor",
    ownerRole: "shared",
    title: "1. Bilagor",
    legalReference: "AFS 2023:3 kap. 4",
    explanation:
      "Lista över bilagor som är relevanta för arbetsmiljöarbetet inom projektet.",
    source: "bilagor",
    docRefs: [
      { doc: "10", title: "Arbetsområdesutredning", link: "arbetsomrade" },
      { doc: "11", title: "Risksammanställning", link: "risk" },
      { doc: "13", title: "Särskilda arbetsmiljörisker", link: "risk" },
      { doc: "15", title: "Skydds- och ordningsregler", link: "skyddsregler" },
      { doc: "30", title: "Arbetsberedningar", link: "arbetsberedning" },
      { doc: "31", title: "Bruksskede / funktionskrav", link: "bruksskede" },
    ],
    fields: [
      { key: "appendices", label: "Bilagor", type: "appendixList" },
    ],
  },
  {
    key: "orientering",
    ownerRole: "bas_p",
    title: "2. Orientering om projektet",
    legalReference: "AFS 2023:3 kap. 4",
    explanation:
      "Projektets grunduppgifter, adress, beskrivning, intilliggande verksamheter och viktiga kontakter under byggtiden.",
    source: "project",
    docRefs: [
      { doc: "01", title: "Uppdragsbekräftelse och projektdata", link: "projekt" },
      { doc: "10", title: "Arbetsområdesutredning", link: "arbetsomrade" },
    ],
    fields: [
      { key: "projectNumber", label: "Projektnummer", type: "text" },
      { key: "projectName", label: "Projektnamn", type: "text" },
      { key: "version", label: "Version", type: "text" },
      { key: "status", label: "Status", type: "text" },
      { key: "address", label: "Adress till arbetsplats", type: "text" },
      { key: "postalCode", label: "Postnummer", type: "text" },
      { key: "city", label: "Ort", type: "text" },
      { key: "gps", label: "Postadress / GPS-koordinater", type: "text" },
      { key: "description", label: "Projektbeskrivning", type: "textarea", rows: 6 },
      { key: "otherActivities", label: "Andra verksamheter och projekt", type: "textarea", rows: 4 },
      { key: "fixedOperationContact", label: "Arbete på fast driftställe – samordningsansvarig", type: "contact" },
      { key: "sharedWorkplaceContact", label: "Gemensamt arbetsställe – överordnad funktion", type: "contact" },
      { key: "fireSafetyContact", label: "Brandskyddsansvarig under byggtiden", type: "contact" },
      { key: "permitResponsibleContact", label: "Tillståndsansvarig för arbetstillstånd", type: "contact" },
      { key: "hotWorkPermit", label: "Tillståndsansvarig för heta arbeten", type: "hotwork" },
      { key: "authorizedElectrician", label: "Behörig elinstallatör", type: "contact" },
    ],
  },
  {
    key: "organisation",
    ownerRole: "shared",
    title: "3. Arbetsmiljöorganisation",
    legalReference: "AFS 2023:3 kap. 2–3",
    explanation:
      "Byggherre, ställföreträdare, Bas-P, Bas-U och övrig arbetsplatsorganisation med kontaktuppgifter.",
    source: "organisation",
    docRefs: [{ doc: "01", title: "Uppdragsbekräftelse / roller", link: "projekt" }],
    fields: [
      { key: "client", label: "Byggherre", type: "contact" },
      { key: "clientRepresentative", label: "Byggherrens ställföreträdare", type: "contact" },
      { key: "basP", label: "Byggarbetsmiljösamordnare för planering och projektering (Bas-P)", type: "contact" },
      { key: "basU", label: "Byggarbetsmiljösamordnare för utförandet (Bas-U)", type: "contact" },
      { key: "siteOrganization", label: "Arbetsplatsorganisation", type: "siteOrg" },
    ],
  },
  {
    key: "introduktion",
    ownerRole: "bas_u",
    title: "4. Introduktion på byggarbetsplatsen",
    legalReference: "AFS 2023:3 4 kap. 15 §",
    explanation:
      "Syfte, ansvar, tidpunkt och innehåll för introduktionen av alla som ska arbeta eller vistas på arbetsplatsen.",
    source: "safety_rules",
    docRefs: [{ doc: "15", title: "Skydds- och ordningsregler", link: "skyddsregler" }],
    fields: [
      { key: "purpose", label: "Syfte", type: "textarea", rows: 5, default: "Syftet med introduktionen är att säkerställa att alla personer som ska arbeta eller vistas på byggarbetsplatsen får nödvändig information om arbetsmiljörisker, skyddsregler och säkerhetsrutiner innan arbetet påbörjas." },
      { key: "responsibility", label: "Ansvar", type: "textarea", rows: 3, default: "Bas-U ansvarar för att introduktion genomförs för samtliga personer som ska arbeta på byggarbetsplatsen. Entreprenörer ansvarar för att deras arbetstagare deltar i introduktionen innan arbete påbörjas." },
      { key: "timing", label: "Tidpunkt", type: "textarea", rows: 4, default: "Introduktionen ska genomföras före första arbetsdagen på byggarbetsplatsen. Vid behov ska introduktionen upprepas vid förändringar i projektets arbetsmiljörisker, förändringar i arbetsplatsens organisation eller längre avbrott i arbetet." },
      { key: "content", label: "Innehåll i introduktionen", type: "textarea", rows: 8, default: "Introduktionen ska minst omfatta: genomgång av ordnings- och skyddsregler, information om aktuella arbetsmiljörisker och riskzoner, genomförda och planerade arbetsmiljöåtgärder, rutin för rapportering av arbetsmiljörisker och tillbud, beredskapsplan, utrymningsvägar och återsamlingsplats, arbetsplatsdispositionsplan (APD-plan) samt projektets arbetsmiljöplan och bilagor." },
      { key: "languageAdaptation", label: "Språkanpassning", type: "textarea", rows: 4, default: "Bas-U ansvarar för att introduktionen är begriplig för alla deltagare. Vid behov ska introduktionen anpassas genom muntlig genomgång, användning av bilder eller instruktioner, eller översättning till annat språk." },
      { key: "documentation", label: "Dokumentation", type: "textarea", rows: 3, default: "Deltagande i introduktionen dokumenteras genom signering av introduktionslista eller närvarolista. Dokumentationen förvaras på platskontoret eller i projektets dokumenthanteringssystem." },
      { key: "nonCompliance", label: "Konsekvens vid bristande efterlevnad", type: "textarea", rows: 3, default: "Den som inte följer ordnings- och skyddsreglerna eller projektets arbetsmiljöplan kan avvisas från byggarbetsplatsen." },
    ],
  },
  {
    key: "moten",
    ownerRole: "bas_u",
    title: "5. Rutin för arbetsmiljöfrågor på möten",
    legalReference: "AFS 2023:3",
    explanation:
      "Hur arbetsmiljöfrågor tas upp på byggmöten, samordningsmöten och säkerhetsmöten.",
    source: "meetings",
    docRefs: [{ doc: "23", title: "Samordningsmöten Bas-P/Bas-U", link: "samordning" }],
    fields: [
      { key: "buildingMeetings", label: "Byggmöten", type: "textarea", rows: 6, default: "Arbetsmiljöfrågor ska vara en stående punkt på dagordningen vid samtliga byggmöten. Bas-U ansvarar för att redovisa aktuella arbetsmiljörisker, genomförda och planerade skyddsåtgärder, inträffade tillbud eller avvikelser samt behov av ytterligare samordning mellan entreprenörer. Protokoll från byggmöten ska upprättas och arkiveras." },
      { key: "coordinationMeetings", label: "Samordningsmöten", type: "textarea", rows: 5, default: "Bas-U ska vid behov kalla till samordningsmöten mellan entreprenörer när arbeten påverkar varandra eller innebär gemensamma arbetsmiljörisker. Exempel är parallella arbeten i samma arbetsområde, arbeten som påverkar trafik eller logistik, och arbeten som innebär särskilda risker. Skyddsombud ska ges möjlighet att delta." },
      { key: "safetyMeetings", label: "Säkerhetsmöten", type: "textarea", rows: 5, default: "Inför särskilt riskfyllda arbeten ska säkerhetsmöten hållas innan arbetet startar. Exempel är större lyftoperationer, rivningsarbeten, sprängningsarbeten och arbeten i trånga utrymmen. Syftet är att säkerställa att alla berörda aktörer har en gemensam förståelse för arbetsmetod, risker och skyddsåtgärder. Deltagande ska dokumenteras." },
    ],
  },
  {
    key: "kontroll",
    ownerRole: "bas_u",
    title: "6. Rutin för kontroll och uppföljning av arbetsmiljöarbetet",
    legalReference: "AFS 2023:3",
    explanation:
      "Skyddsronder, stickprovskontroller, uppföljning av entreprenörer och struktur för riskhantering.",
    source: "followup",
    docRefs: [{ doc: "21", title: "Uppföljning av utförandet", link: "utforande" }],
    fields: [
      { key: "safetyRounds", label: "Skyddsronder", type: "textarea", rows: 6, default: "Skyddsronder ska genomföras regelbundet under projektets genomförande. Vid skyddsronder ska arbetsplatsen kontrolleras med avseende på efterlevnad av arbetsmiljöplanen, ordning och säkerhet på arbetsplatsen samt aktuella arbetsmiljörisker. Skyddsombud ska ges möjlighet att delta. Identifierade brister ska dokumenteras och åtgärder ska tidsättas." },
      { key: "spotChecks", label: "Stickprovskontroller", type: "textarea", rows: 5, default: "Bas-U genomför löpande stickprovskontroller för att säkerställa att entreprenörerna följer arbetsmiljöbestämmelser och arbetsmiljöplanen, har genomfört riskbedömningar inför sina arbeten och innehar erforderliga tillstånd, besiktningsintyg och utbildningsintyg." },
      { key: "contractorFollowUp", label: "Uppföljning av entreprenörernas arbetsmiljöarbete", type: "textarea", rows: 5, default: "Innan en entreprenör får påbörja arbete ska entreprenören redovisa för Bas-U: arbeten med särskild risk, arbetsmiljörisker som det egna arbetet kan orsaka för andra, och egna arbetsmiljörisker som kan behöva samordnas med andra entreprenörer." },
      { key: "riskStructure", label: "Struktur för riskhantering i projektet", type: "textarea", rows: 6, default: "Risker som identifieras under planering och projektering dokumenteras i projektets risksammanställning (Dokument 11) och ligger till grund för arbetsmiljöplanen. Under byggskedet hanteras mer detaljerade risker kopplade till enskilda arbetsmoment genom arbetsberedningar. Om nya risker påverkar projektets övergripande riskbild ska dessa vid behov föras in i risksammanställningen." },
    ],
  },
  {
    key: "sarskilda_risker",
    ownerRole: "shared",
    title: "7. Särskilda arbetsmiljörisker",
    legalReference: "AFS 2023:3 kap. 4 och bilaga",
    explanation:
      "De 13 arbetena med särskild risk enligt AFS 2023:3. Markera Ja/Nej och beskriv åtgärder för de risker som är aktuella.",
    source: "sarskilda",
    docRefs: [
      { doc: "13", title: "Särskilda arbetsmiljörisker", link: "risk" },
      { doc: "11", title: "Risksammanställning", link: "risk" },
    ],
    fields: [
      { key: "risks", label: "Särskilda risker", type: "riskChecklist" },
    ],
  },
  {
    key: "arbetsberedningar",
    ownerRole: "bas_u",
    title: "8. Arbetsberedningar",
    legalReference: "AFS 2023:3",
    explanation:
      "Entreprenörens ansvar för arbetsberedningar, innehåll och signering.",
    source: "work_preparations",
    docRefs: [
      { doc: "30", title: "Arbetsberedningar", link: "arbetsberedning" },
      { doc: "31", title: "Samlingsdokument arbetsberedningar", link: "arbetsberedning" },
    ],
    fields: [
      { key: "description", label: "Arbetsberedningar", type: "textarea", rows: 10, default: "Entreprenören ansvarar för att upprätta och genomföra skriftliga arbetsberedningar med tillhörande riskanalys för arbeten inom projektet som bedöms ha förhöjd eller hög risk. Arbetsberedningen ska beskriva arbetsmoment och arbetsmetod, identifierade arbetsmiljörisker, planerade skyddsåtgärder, ansvar för genomförande och kontroll. Bas-U granskar arbetsberedningen och lämnar klartecken för start av arbetet." },
      { key: "signatures", label: "Signaturer", type: "signatureList" },
    ],
  },
];

export type AmpPlanStatus = "draft" | "active" | "revised" | "archived";

export const AMP_PLAN_STATUS_LABELS: Record<AmpPlanStatus, string> = {
  draft: "Utkast",
  active: "Gällande",
  revised: "Reviderad",
  archived: "Arkiverad",
};

/** Skapar ett tomt men typat strukturerat data-objekt för ett avsnitt. */
export function getDefaultStructuredData(def: AmpSectionDef): Record<string, any> {
  const data: Record<string, any> = {};
  for (const field of def.fields || []) {
    if (field.type === "appendixList") {
      data[field.key] = (def.docRefs || []).map((ref) => ({
        name: ref.title,
        doc: ref.doc,
        included: false,
        note: "",
      }));
    } else if (field.type === "riskChecklist") {
      data[field.key] = AMP_SPECIAL_RISKS.map((r) => ({ ...r }));
    } else if (field.type === "siteOrg" || field.type === "signatureList") {
      data[field.key] = [];
    } else if (field.type === "contact" || field.type === "hotwork") {
      data[field.key] = {};
    } else if (field.type === "textarea" || field.type === "text") {
      data[field.key] = field.default ?? "";
    }
  }
  return data;
}

/** Malltext för de 13 särskilda riskerna enligt AFS 2023:3 bilaga */
export const AMP_SPECIAL_RISKS: AmpSpecialRisk[] = [
  { id: "fall", text: "Arbete där det finns risk att falla till en lägre nivå, om nivåskillnaden är två meter eller mer", applicable: null },
  { id: "begravning", text: "Arbete där det finns risk att begravas under jordmassor, eller sjunka ned i lös mark", applicable: null },
  { id: "tunga", text: "Arbete med lansering, montering eller nedmontering av tunga byggelement, eller tunga formbyggnadselement", applicable: null },
  { id: "kemiska", text: "Arbete som utsätter någon för kemiska eller biologiska ämnen som medför särskild fara för hälsa och säkerhet, eller omfattas av krav på medicinsk kontroll", applicable: null },
  { id: "rivning", text: "Rivning av bärande konstruktioner, hälsofarliga material eller ämnen", applicable: null },
  { id: "hogspanning", text: "Arbete i närheten av högspänningsledningar", applicable: null },
  { id: "trafik", text: "Arbete på en plats eller ett område där fordonstrafik passerar", applicable: null },
  { id: "sprangning", text: "Arbete där sprängämnen används", applicable: null },
  { id: "brunnar", text: "Arbete i brunnar och tunnlar samt anläggningsarbete under jord", applicable: null },
  { id: "drunkna", text: "Arbete där det finns risk att drunkna", applicable: null },
  { id: "dykning", text: "Arbete som utförs under vatten med dykarutrustning", applicable: null },
  { id: "kassun", text: "Arbete som utförs i en kassun under förhöjt lufttryck", applicable: null },
  { id: "stralning", text: "Arbete i närheten av joniserande strålning och enligt Strålsäkerhetsmyndighetens föreskrifter ska inrätta ett kontrollerat område eller ett skyddat område", applicable: null },
];
